/**
 * Shared password policy for ALL signup flows (standard + extended).
 * Keep the rules and the UI checklist in sync by driving both from
 * PASSWORD_REQUIREMENTS.
 */

export interface PasswordRequirement {
  id: string;
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { id: 'length', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { id: 'uppercase', label: 'An uppercase letter (A-Z)', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lowercase', label: 'A lowercase letter (a-z)', test: (pw) => /[a-z]/.test(pw) },
  { id: 'number', label: 'A number (0-9)', test: (pw) => /\d/.test(pw) },
];

export function isPasswordStrongEnough(password: string): boolean {
  return PASSWORD_REQUIREMENTS.every((r) => r.test(password));
}

export interface PasswordStrength {
  /** 0–4: how many requirements are met (drives the meter). */
  score: number;
  label: 'Weak' | 'Fair' | 'Good' | 'Strong';
  /** Tailwind-friendly color for the meter + label. */
  color: string;
}

export function getPasswordStrength(password: string): PasswordStrength {
  if (!password) return { score: 0, label: 'Weak', color: '#ef4444' };
  let score = PASSWORD_REQUIREMENTS.filter((r) => r.test(password)).length;
  // Extra credit for long or symbol-bearing passwords (display only).
  if (score === PASSWORD_REQUIREMENTS.length && (password.length >= 12 || /[^A-Za-z0-9]/.test(password))) {
    score = PASSWORD_REQUIREMENTS.length;
  }
  if (score <= 1) return { score, label: 'Weak', color: '#ef4444' };
  if (score === 2) return { score, label: 'Fair', color: '#f59e0b' };
  if (score === 3) return { score, label: 'Good', color: '#eab308' };
  return { score, label: 'Strong', color: '#22c55e' };
}
